import React, { useMemo, useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateSweepDetails } from "../../api/agency-profile";
import { logDetails } from "../../api/logger-configuration";
import '@gdk/button-switch/dist/styles/gdk-button-switch.min.css';
import PropTypes from "prop-types";
import { EMAIL_WITH_TLD_REGEX } from "../../constants/ValidationConstants";

const validationSchema = yup.object({
    sweepEmail: yup.string()
        .nullable()
        .email("Please enter a valid email address.")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
        .required("Email address is required."),
});

export default function EmailForm({
    agencyProfile,
    setAgencyProfile,
    branchProfile,
    setAgencyBranches,
    isBranch,
    onSave,
    onCancel,
    isAdditionalEmail = false,
    additionalEmailIndex = null,
    isNewEmail = false
}) {
    const originalEmail = useMemo(() => {
        if (isNewEmail) {
            return "";
        }
        if (isAdditionalEmail && additionalEmailIndex !== null) {
            return agencyProfile?.sweepDetails?.AdditionalSweepEmails?.[additionalEmailIndex]?.emailAddress;
        }
        return agencyProfile?.sweepDetails?.SweepsEmail ?? "";
    }, [agencyProfile, isAdditionalEmail, additionalEmailIndex, isNewEmail]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        clearErrors,
        setError,
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { sweepEmail: originalEmail }
    });

    const gdsCheckboxButtonGroupRef = useRef();
    const [saveClicked, setSaveClicked] = useState(false);
    const [makePrimaryEmail, setMakePrimaryEmail] = useState(false);

    const handlePrimaryEmailChange = (event) => {
        const checkedValues = event.detail?.value || [];
        const isChecked = checkedValues.length > 0;
        setMakePrimaryEmail(isChecked);
    };

    useEffect(() => {
        gdsCheckboxButtonGroupRef.current?.addEventListener("value-change", handlePrimaryEmailChange);
        return () => {
            gdsCheckboxButtonGroupRef.current?.removeEventListener("value-change", handlePrimaryEmailChange);
        };
    }, [handlePrimaryEmailChange]);

    // Helper function to check for duplicate emails
    const isDuplicateEmail = (emailToCheck) => {
        if (!emailToCheck || !agencyProfile?.sweepDetails) {
            return false;
        }

        const emailLower = emailToCheck.toLowerCase().trim();

        // Check against primary email (only if adding or editing an additional/new email)
        if (isAdditionalEmail || isNewEmail) {
            const primaryEmail = agencyProfile.sweepDetails.SweepsEmail;
            if (primaryEmail && primaryEmail.toLowerCase().trim() === emailLower) {
                return true;
            }
        }

        // Check against additional emails
        const additionalEmails = agencyProfile.sweepDetails.AdditionalSweepEmails || [];
        for (let i = 0; i < additionalEmails.length; i++) {
            const additionalEmail = additionalEmails[i]?.emailAddress;

            // Skip the current email being edited
            if (isAdditionalEmail && additionalEmailIndex === i) {
                continue;
            }

            if (additionalEmail && additionalEmail.toLowerCase().trim() === emailLower) {
                return true;
            }
        }

        return false;
    };

    const submitSuccessCallback = (updatedProfile) => {
        if (isBranch) {
            setAgencyBranches(prevBranches => prevBranches.map(branch => branch.agencyId === branchProfile.agencyId ? updatedProfile : branch));
            logDetails("Sucessfully updated sweep email for branch:" + updatedProfile.agencyId);
            onSave();
        }
        else {
            setAgencyProfile(updatedProfile);
            logDetails("Sucessfully updated sweep email for agency:" + updatedProfile.agencyId);
            onSave();
        }
        setMakePrimaryEmail(false);
    };
    const submitFinallyCallback = () => {
        setSaveClicked(false);
        if (isNewEmail) {
            reset({ sweepEmail: "" });
        }
        setMakePrimaryEmail(false);
        onCancel();
    };

    const handleCancel = () => {
        reset({ sweepEmail: originalEmail });
        setMakePrimaryEmail(false);
        clearErrors();
        onCancel();
    };
    // Helper function for baseProfile
    const createBaseProfile = () => {
        const baseProfile = isBranch ? branchProfile : agencyProfile;
        return {
            ...baseProfile,
            ivans: baseProfile.ivans ?? {},
            sweepDetails: {
                ...baseProfile.sweepDetails
            },
        };
    };

    const createEmailObject = (emailAddress) => ({
        emailType: "Additional",
        emailAddress
    });
    const setAsPrimaryEmail = (profile, newPrimaryEmail, targetIndex = null) => {
        const currentPrimaryEmail = profile.sweepDetails.SweepsEmail;
        const additionalEmails = [...(profile.sweepDetails.AdditionalSweepEmails || [])];

        // Set new primary email
        profile.sweepDetails.SweepsEmail = newPrimaryEmail.sweepEmail;

        // Move old primary to additional emails
        if (currentPrimaryEmail) {
            if (targetIndex !== null) {
                // Replace specific additional email with old primary
                additionalEmails[targetIndex] = createEmailObject(currentPrimaryEmail);
            } else {
                // Add old primary as new additional email
                additionalEmails.push(createEmailObject(currentPrimaryEmail));
            }
        }

        profile.sweepDetails.AdditionalSweepEmails = additionalEmails;
        return profile;
    };
    // Helper function to create updated agency profile based on email type
    const createUpdatedAgencyProfile = (values) => {
        const updatedProfile = createBaseProfile();

        if (makePrimaryEmail) {
            return setAsPrimaryEmail(updatedProfile, values, additionalEmailIndex);
        }

        if (isNewEmail) {
            const currentAdditionalEmails = updatedProfile?.sweepDetails?.AdditionalSweepEmails || [];
            updatedProfile.sweepDetails.AdditionalSweepEmails = [...currentAdditionalEmails, createEmailObject(values.sweepEmail)];  // Add as additional email
        } else if (isAdditionalEmail && additionalEmailIndex !== null) {
            const updatedAdditionalEmails = [...(updatedProfile?.sweepDetails?.AdditionalSweepEmails || [])];
            // Update the additional email
            updatedAdditionalEmails[additionalEmailIndex] = {
                ...updatedAdditionalEmails[additionalEmailIndex],
                emailAddress: values.sweepEmail
            };
            updatedProfile.sweepDetails.AdditionalSweepEmails = updatedAdditionalEmails;
        }
        else {
            updatedProfile.sweepDetails.SweepsEmail = values.sweepEmail;
        }
        return updatedProfile;
    };

    // Validation helpers
    const hasNoChanges = (values) => {
        return !isNewEmail &&
            values.sweepEmail === originalEmail &&
            makePrimaryEmail !== true;
    };
    const onSubmit = (values) => {
        // Check if no changes were made
        if (hasNoChanges(values)) {
            logDetails("No changes detected in sweep email - skipping update");
            handleCancel();
            return;
        }

        // Check for duplicate email
        if (isDuplicateEmail(values.sweepEmail)) {
            setError("sweepEmail", {
                type: "manual",
                message: "This email address has already been added."
            });
            setSaveClicked(false);
            return;
        }

        setSaveClicked(true);
        const updatedProfile = createUpdatedAgencyProfile(values);

        try {
            updateSweepDetails(
                updatedProfile,
                () => submitSuccessCallback(updatedProfile),
                submitFinallyCallback
            );
        } catch (error) {
            logDetails("Error updating sweep details: " + error.message);
            setSaveClicked(false);
        }
    };

    return (
        <form className="form" style={{ textAlign: "left" }}>
            <div className={`form-field large-text-input ${errors.sweepEmail ? 'form-field--error' : ''}`}>
                <label htmlFor="personal-email" className="text">
                    <h5>Email Address</h5>
                </label>
                <input id="personal-email" type="email" size="40" {...register("sweepEmail")} />
                {errors.sweepEmail &&
                    <span className="form-message">{errors.sweepEmail.message}</span>
                }
            </div>
            {(isAdditionalEmail || isNewEmail) && (
                <gds-checkbox-button-group
                    ref={gdsCheckboxButtonGroupRef}
                    fieldset-aria-labelledby="my-aria-labelledby"
                    group-id="radio-button-group"
                    data-testid="make-primary-checkbox-group"
                >
                    <gds-checkbox-button
                        input-id="makePrimaryEmail"
                        value={makePrimaryEmail ? ["makePrimaryEmail"] : []}
                        checked={makePrimaryEmail ? true : null}
                        data-testid="make-primary-checkbox"
                    >
                        <span slot="label">
                            Set it as agency primary email
                        </span>
                    </gds-checkbox-button>
                </gds-checkbox-button-group>
            )}
            <div className="row">
                <div className="col-sm-12">
                    <button
                        onClick={handleSubmit(onSubmit)}
                        disabled={saveClicked}
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                    >
                        <span>Save</span>
                    </button>
                    <button
                        type="button"
                        disabled={saveClicked}
                        className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn"
                        onClick={handleCancel}
                    >
                        <span>Cancel</span>
                    </button>
                </div>
            </div>
        </form>

    )
};

EmailForm.propTypes = {
    agencyProfile: PropTypes.object.isRequired,
    setAgencyProfile: PropTypes.func.isRequired,
    branchProfile: PropTypes.object,
    setAgencyBranches: PropTypes.func,
    isBranch: PropTypes.bool,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    isAdditionalEmail: PropTypes.bool,
    additionalEmailIndex: PropTypes.number,
    isNewEmail: PropTypes.bool
};